/**
 * Native bridge — only loaded inside the Capacitor iOS / Android shell.
 * Web browsers safely fall through to the existing Service Worker push flow.
 */
import { Capacitor } from "@capacitor/core";
import { api } from "./api";

export const isNative = () => Capacitor.isNativePlatform();

/**
 * Native iOS push registration (APNS).
 * Sends the device token to /api/push/subscribe so the same backend route
 * works for both Web Push and Native Push. The backend treats native tokens
 * by storing them under `keys: { native: true, token }`.
 */
export async function registerNativePush() {
  if (!isNative()) return null;
  const { PushNotifications } = await import("@capacitor/push-notifications");

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return { status: "denied" };

  return new Promise((resolve) => {
    PushNotifications.addListener("registration", async (token) => {
      try {
        await api.post("/push/subscribe", {
          endpoint: `apns:${token.value}`,
          keys: { native: true, platform: "ios", token: token.value },
        });
        resolve({ status: "registered" });
      } catch (e) {
        resolve({ status: "failed", error: String(e) });
      }
    });
    PushNotifications.addListener("registrationError", (err) => {
      resolve({ status: "failed", error: err.error });
    });
    // Optional: handle tap → deep link
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const url = action?.notification?.data?.url || "/today";
      try { window.location.assign(url); } catch { /* ignore */ }
    });

    PushNotifications.register();
  });
}

/** Set the iOS status bar style to match Sister Signal's current theme. */
export async function applyNativeStatusBar(theme) {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: theme === "night" ? Style.Light : Style.Dark });
    await StatusBar.setBackgroundColor({
      color: theme === "night" ? "#0F0F13" : "#FDFBF7",
    });
  } catch {
    /* ignore on web */
  }
}

/** Hide the launch splash gracefully after first paint. */
export async function hideNativeSplash() {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 350 });
  } catch { /* ignore */ }
}

/** Lightweight haptic — used for discreet feedback (Quick Exit, breath cycle). */
export async function softHaptic() {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* ignore */ }
}

// --------------------------------------------------------------------------- //
// Sign in with Apple — unified helper                                         //
// --------------------------------------------------------------------------- //

const APPLE_SERVICES_ID =
  process.env.REACT_APP_APPLE_SERVICES_ID || "app.sistersignal.web";

/**
 * Returns { identity_token, first_name, last_name, email } on success.
 * Native iOS uses the @capacitor-community/apple-sign-in plugin (no setup
 * needed beyond enabling the capability in Xcode). Web falls back to Apple's
 * AppleID JS SDK and requires a Services ID + return URL configured in your
 * Apple Developer account. See /app/frontend/ios/README.md.
 */
export async function signInWithApple() {
  if (isNative()) {
    // Native iOS uses the app's bundle id + the production marketing
    // domain for the Apple sign-in return URL. The redirect URI must
    // exactly match a Service ID return URL configured in your Apple
    // Developer account, so it is read from env at build time. Set
    // REACT_APP_APPLE_REDIRECT_URI in frontend/.env to your production
    // value (e.g. https://sistersignal.org/auth/apple-redirect). A safe
    // fallback to the current origin keeps preview/dev working without
    // any code change.
    const nativeRedirect =
      process.env.REACT_APP_APPLE_REDIRECT_URI ||
      (typeof window !== "undefined" && window.location && window.location.origin
        ? `${window.location.origin}/auth/apple-redirect`
        : "");
    const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
    const result = await SignInWithApple.authorize({
      clientId: "app.sistersignal.ios",
      redirectURI: nativeRedirect,
      scopes: "email name",
      // nonce & state recommended in production; the Apple SDK validates them
    });
    const r = result?.response || {};
    return {
      identity_token: r.identityToken,
      first_name: r.givenName,
      last_name: r.familyName,
      email: r.email,
      source: "native",
    };
  }

  // Web flow — lazy-load Apple's JS SDK once
  if (!window.AppleID) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
      s.async = true;
      s.defer = true;
      s.onload = resolve;
      s.onerror = () => reject(new Error("Couldn’t load Apple sign-in."));
      document.head.appendChild(s);
    });
  }
  window.AppleID.auth.init({
    clientId: APPLE_SERVICES_ID,
    scope: "name email",
    redirectURI: window.location.origin + "/auth/apple-redirect",
    usePopup: true,
  });
  const out = await window.AppleID.auth.signIn();
  const id = out?.authorization?.id_token;
  const user = out?.user || {};
  return {
    identity_token: id,
    first_name: user?.name?.firstName,
    last_name: user?.name?.lastName,
    email: user?.email,
    source: "web",
  };
}
